/* ============================================================
   LB-CART-DRAWER.JS — Drawer panier · Dawn-style async
   Utilise Cart engine + QtyController de lb-cart.js
   ============================================================ */
'use strict';

const CartDrawer = {
  el:      null,
  overlay: null,
  isOpen:  false,

  init() {
    this.el      = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-overlay');
    if (!this.el) return;

    // Open triggers
    document.querySelectorAll('[data-cart-open]').forEach(btn =>
      btn.addEventListener('click', e => { e.preventDefault(); this.toggle(); })
    );
    // Close triggers
    this.el.querySelector('[data-drawer-close]')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && this.isOpen) this.close(); });

    // Délégation événements dans le drawer
    this.el.addEventListener('click', e => {
      // +/-
      const qtyBtn = e.target.closest('[data-cd-qty-btn]');
      if (qtyBtn) {
        const item = qtyBtn.closest('[data-cd-item]');
        if (!item) return;
        const ctrl = this._getCtrl(item);
        if (ctrl) ctrl.step(qtyBtn.dataset.cdQtyBtn);
        return;
      }
      // Supprimer
      const rmBtn = e.target.closest('[data-cd-remove]');
      if (rmBtn) {
        const item = rmBtn.closest('[data-cd-item]');
        if (!item) return;
        const ctrl = this._getCtrl(item);
        if (ctrl) ctrl.setTo(0);
        return;
      }
      // Upsell add
      const upsellBtn = e.target.closest('[data-upsell-add]');
      if (upsellBtn) {
        const vid = parseInt(upsellBtn.dataset.variantId);
        if (!vid) return;
        window.LB?.Cart?.addItem(vid, 1, upsellBtn);
      }
    });
  },

  _getCtrl(item) {
    const Cart     = window.LB?.Cart;
    if (!Cart) return null;
    const key      = item.dataset.key;
    const maxQty   = parseInt(item.dataset.maxQty || '99');
    const qtyEl    = item.querySelector('[data-cd-qty-val]');
    const minusBtn = item.querySelector('[data-cd-qty-btn="minus"]');
    const plusBtn  = item.querySelector('[data-cd-qty-btn="plus"]');
    const initQty  = parseInt(qtyEl?.dataset.qty || '1');
    if (!key) return null;
    return Cart.getController(key, { initialQty: initQty, maxQty, qtyEl, minusBtn, plusBtn });
  },

  open() {
    if (!this.el) return;
    this.isOpen = true;
    this.el.classList.add('open');
    this.overlay?.classList.add('open');
    document.body.style.overflow = 'hidden';
    this.el.setAttribute('aria-hidden', 'false');
  },

  close() {
    if (!this.el) return;
    this.isOpen = false;
    this.el.classList.remove('open');
    this.overlay?.classList.remove('open');
    document.body.style.overflow = '';
    this.el.setAttribute('aria-hidden', 'true');
  },

  toggle() { this.isOpen ? this.close() : this.open(); },

  /* ── Render items — smart diff ──────────────────────────── */
  renderItems(cart) {
    if (!this.el) return;
    const itemsWrap = this.el.querySelector('[data-cd-items]');
    const emptyEl   = this.el.querySelector('[data-cd-empty]');
    const footerEl  = this.el.querySelector('[data-cd-footer]');
    const shipEl    = this.el.querySelector('[data-cd-ship]');
    const countEl   = this.el.querySelector('[data-drawer-count]');

    if (countEl) countEl.textContent = `(${cart.item_count})`;

    if (!cart.item_count) {
      if (itemsWrap) itemsWrap.innerHTML = '';
      if (emptyEl)  emptyEl.style.display  = 'flex';
      if (footerEl) footerEl.style.display  = 'none';
      if (shipEl)   shipEl.style.display    = 'none';
      return;
    }

    if (emptyEl)  emptyEl.style.display  = 'none';
    if (footerEl) footerEl.style.display = 'block';
    if (shipEl)   shipEl.style.display   = 'block';
    if (!itemsWrap) return;

    const existingKeys = new Set([...itemsWrap.querySelectorAll('[data-cd-item]')].map(el => el.dataset.key));
    const cartKeys     = new Set(cart.items.map(i => i.key));
    const needsRebuild = [...existingKeys].some(k => !cartKeys.has(k)) ||
                         [...cartKeys].some(k => !existingKeys.has(k));

    if (needsRebuild) {
      itemsWrap.innerHTML = cart.items.map(item => this._itemHTML(item)).join('');
      // Invalide les controllers pour les items reconstruits
      // (ils seront recréés au prochain clic avec l'état correct)
      cart.items.forEach(item => {
        window.LB?.Cart?._controllers?.delete(item.key);
      });
    } else {
      // Update uniquement les données qui ont changé côté serveur
      cart.items.forEach(item => {
        const el    = itemsWrap.querySelector(`[data-cd-item][data-key="${item.key}"]`);
        if (!el) return;

        // Prix de ligne
        const priceEl = el.querySelector('[data-cd-line-price]');
        if (priceEl) priceEl.textContent = window.LB?.formatMoney?.(item.line_price) || '';

        // Sync controller (respecte l'état optimiste si pending)
        const ctrl = window.LB?.Cart?._controllers?.get(item.key);
        if (ctrl) {
          ctrl.updateConfirmed(item.quantity, item.variant?.inventory_quantity || 99);
        } else {
          // Pas de controller actif → update direct du DOM
          const qtyEl = el.querySelector('[data-cd-qty-val]');
          if (qtyEl && !qtyEl.classList.contains('qty--syncing')) {
            const qn = qtyEl.querySelector('.qn');
            if (qn) qn.textContent = item.quantity;
            qtyEl.dataset.qty = item.quantity;
          }
          const plusBtn = el.querySelector('[data-cd-qty-btn="plus"]');
          if (plusBtn) plusBtn.disabled = item.quantity >= (item.inventory_quantity || 99);
        }
      });
    }

    // Total drawer
    const totalEl = this.el.querySelector('[data-drawer-total]');
    if (totalEl) totalEl.textContent = window.LB?.formatMoney?.(cart.total_price) || '';
  },

  _itemHTML(item) {
    const imgSrc = item.featured_image?.url
      ? item.featured_image.url.replace(/(\.[a-z]+)(\?|$)/, '_80x80$1$2')
      : null;
    const variantHtml = item.variant_title && item.variant_title !== 'Default Title'
      ? `<div class="cd-item__var">${item.variant_title}</div>` : '';
    const imgHtml = imgSrc
      ? `<a href="${item.url}"><img src="${imgSrc}" alt="${item.product_title}" class="cd-item__img" loading="lazy" width="68" height="68"></a>`
      : `<a href="${item.url}"><div class="cd-item__img-ph"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></div></a>`;
    const maxQty = item.inventory_quantity || 99;
    const fm     = window.LB?.formatMoney || (c => (c / 100).toFixed(2) + '€');

    return `
      <div class="cd-item" data-cd-item data-key="${item.key}" data-max-qty="${maxQty}">
        ${imgHtml}
        <div class="cd-item__body">
          <div class="cd-item__title"><a href="${item.url}">${item.product_title}</a></div>
          ${variantHtml}
          <div class="cd-item__foot">
            <div>
              <div class="cd-item__price" data-cd-line-price>${fm(item.line_price)}</div>
              <div class="cd-item__unit">${fm(item.final_price)} / u</div>
            </div>
            <div style="display:flex;align-items:center;gap:6px;">
              <div class="cd-qty">
                <button class="cd-qty__btn" data-cd-qty-btn="minus" aria-label="−">−</button>
                <div class="cd-qty__val" data-cd-qty-val data-qty="${item.quantity}">
                  <span class="qn">${item.quantity}</span>
                </div>
                <button class="cd-qty__btn" data-cd-qty-btn="plus" aria-label="+"
                  ${item.quantity >= maxQty ? 'disabled' : ''}>+</button>
              </div>
              <button class="cd-item__remove" data-cd-remove aria-label="Supprimer">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
              </button>
            </div>
          </div>
        </div>
      </div>`;
  }
};

document.addEventListener('DOMContentLoaded', () => CartDrawer.init());

window.LB = window.LB || {};
window.LB.CartDrawer = CartDrawer;